import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getApiUrl } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";

const Checkout = () => {
  const location = useLocation();

  const products = location.state?.products || [];
  const totalPrice = location.state?.totalPrice || 0;
  const finalPrice = location.state?.finalPrice || totalPrice;
  const discount =
    location.state?.totalDiscount ??
    (location.state?.totalPrice || 0) - (location.state?.finalPrice || 0);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deliveryRules, setDeliveryRules] = useState([]);
  const [shippingPrice, setShippingPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [isDiscountApplied, setIsDiscountApplied] = useState(false);

  const totalDiscountAmount = discount + appliedDiscount;
  const subtotalAfterDiscount = Math.max(totalPrice - totalDiscountAmount, 0);
  const [allProductsAvailable, setAllProductsAvailable] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [gatewaySettings, setGatewaySettings] = useState({
    phonePeEnabled: true,
    razorpayEnabled: false,
  });

  useEffect(() => {
    const fetchGatewaySettings = async () => {
      try {
        const res = await fetch(getApiUrl("payment-settings"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data.success) {
          setGatewaySettings({
            phonePeEnabled: data.data.phonePeEnabled,
            razorpayEnabled: data.data.razorpayEnabled,
          });
        }
      } catch {
        console.error("Failed to load payment settings");
      }
    };
    fetchGatewaySettings();
  }, []);

  useEffect(() => {
    checkProductsStatus(); // Check once

    const interval = setInterval(() => {
      checkProductsStatus(); // Check every 2 mins
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const guestId =
    localStorage.getItem("userId") ||
    (() => {
      const id =
        "guest_" + Date.now() + "_" + Math.floor(Math.random() * 10000);
      localStorage.setItem("userId", id);
      return id;
    })();

  useEffect(() => {
    const fetchDeliveryRules = async () => {
      try {
        const res = await fetch(getApiUrl("delivery-list"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const result = await res.json();
        if (result.success) setDeliveryRules(result.data);
      } catch {
        toast.error("Failed to load delivery rules");
      }
    };

    fetchDeliveryRules();
  }, []);
  useEffect(() => {
    const savedCode = sessionStorage.getItem("discountCode");
    const savedAmount = sessionStorage.getItem("appliedDiscount");

    if (savedCode && savedAmount) {
      setDiscountCode(savedCode);
      setAppliedDiscount(Number(savedAmount));
      setIsDiscountApplied(true);
    }
  }, []);

  const calculateShipping = (stateName) => {
    if (!stateName || !deliveryRules.length) return 0;

    let price = 0;

    const totalQty = products.reduce(
      (sum, item) => sum + Number(item.qty || 0),
      0,
    );

    for (let rule of deliveryRules) {
      const normalized = rule.states.map((s) => s.trim().toLowerCase());

      if (normalized.includes(stateName.trim().toLowerCase())) {
        const baseCharge = Number(rule.baseCharge) || 0;
        const perKgCharge = Number(rule.perKgCharge) || 0;
        const piecesPerKg = Number(rule.piecesPerKg) || 3;

        price = baseCharge;

        if (totalQty > 2) {
          const chargeablePieces = totalQty - 2;
          const blocks = Math.ceil(chargeablePieces / piecesPerKg);
          price += blocks * perKgCharge;
        }

        break;
      }
    }

    setShippingPrice(price || 0);
    return price;
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      state: "",
      district: "",
      address: "",
      email: "",
      pincode: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string()
        .matches(/^\d{10}$/, "Enter valid 10 digit number")
        .required("Phone is required"),
      state: Yup.string().required("Please select a state"),
      district: Yup.string().required("Please select a district"),
      address: Yup.string().required("Address is required"),
      pincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Enter valid 6 digit pincode")
        .required("Pincode is required"),
      email: Yup.string().email("Invalid email"),
    }),
    onSubmit: async (values) => {
      if (!formik.isValid) {
        toast.error("Please fill all required fields correctly!");
        return;
      }

      try {
        const res = await fetch(getApiUrl("checkout-add"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...values, guestId }),
        });

        const result = await res.json();

        if (result.success) {
          if (result.userId) localStorage.setItem("userId", result.userId);

          toast.success(editMode ? "Details updated" : "Checkout saved");

          setUserDetails(values);
          setEditMode(false);
        } else {
          toast.error(result.message || "Checkout failed");
        }
      } catch {
        toast.error("Server error");
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resStates = await fetch(getApiUrl("state-ui-list"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const statesResult = await resStates.json();
        if (statesResult.success) setStates(statesResult.data);

        const resUser = await fetch(getApiUrl("user/get"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: userId }),
        });
        const userResult = await resUser.json();

        if (userResult.success) {
          const userData = userResult.data;
          setUserDetails(userData);

          formik.setValues({
            name: userData.name || "",
            phone: userData.phone || "",
            email: userData.email || "",
            state: userData.state || "",
            district: userData.district || "",
            address: userData.address || "",
            pincode: userData.pincode || "",
          });

          const stateObj = statesResult.data.find(
            (s) => s.state === userData.state,
          );
          setDistricts(
            stateObj ? stateObj.districts : [userData.district || ""],
          );
        }
      } catch {
        toast.error("Failed to load user data");
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (userDetails?.state && deliveryRules.length) {
      calculateShipping(userDetails.state);
    }
  }, [deliveryRules, userDetails]);

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    formik.setFieldValue("state", selectedState);
    formik.setFieldValue("district", "");

    const found = states.find((s) => s.state === selectedState);
    setDistricts(found ? found.districts : []);

    calculateShipping(selectedState);
  };

  const finalPriceWithShipping = subtotalAfterDiscount + shippingPrice;

  // ---------- CAN USER PAY? ----------
  const canPay = !!(userDetails && userDetails.address && allProductsAvailable);

  // ---------- PHONEPE ----------
  const userId = localStorage.getItem("userId");

  const [stockCheckResult, setStockCheckResult] = useState(null);

  const checkProductsStatus = async () => {
    console.log("🛒 PRODUCTS:", products);

    if (!products?.length) {
      setAllProductsAvailable(false);
      return;
    }

    const payload = {
      products: products.map((p, i) => {
        const productId = p._id || p.productId || p.id;
        console.log(`Product ${i}:`, { productId, hasId: !!productId });

        return {
          productId: productId, // ✅ Now guaranteed
          size: p.size,
          qty: p.qty,
        };
      }),
    };

    console.log("📤 SENDING:", payload);

    try {
      const res = await fetch(getApiUrl("check-stock"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setStockCheckResult(data); // ← ADD THIS
      setAllProductsAvailable(data.success && !data.outOfStock?.length);
    } catch (err) {
      setAllProductsAvailable(false);
    }
  };

  const getStockErrorMessage = () => {
    if (!stockCheckResult?.outOfStock?.length) return "";

    return stockCheckResult.outOfStock
      .map((item) => {
        if (item.reason === "inactive") {
          return `❌ "${item.name}" - Product inactive, pick another`;
        } else {
          return `❌ "${item.name}" ${item.size} - Only ${item.available} left (you want ${item.requested})`;
        }
      })
      .join("\n");
  };

  const handlePhonePePayment = async () => {
    if (!userDetails?.address) {
      toast.warning("Please save your address first!");
      return;
    }

    if (!allProductsAvailable) {
      toast.error(`Stock Issue!\n${getStockErrorMessage()}`, {
        autoClose: 8000,
      });
      return;
    }

    try {
      const amountInPaise = Math.round(finalPriceWithShipping * 100);

      const res = await fetch(getApiUrl("create-payment"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountInPaise,
          products,
          userId,
          finalPrice: finalPriceWithShipping,
          address: {
            name: formik.values.name,
            phone: formik.values.phone,
            state: formik.values.state,
            district: formik.values.district,
            address: formik.values.address,
            email: formik.values.email,
            pincode: formik.values.pincode,
          },
        }),
      });

      const data = await res.json();

      if (data.success && data.checkoutPageUrl) {
        sessionStorage.removeItem("discountCode");
        sessionStorage.removeItem("appliedDiscount");
        setAppliedDiscount(0);
        setIsDiscountApplied(false);
        window.location.href = data.checkoutPageUrl;
      } else {
        toast.error(data.error || "Payment error");
      }
    } catch (err) {
      console.error(err);
      toast.error("Payment failed");
    }
  };

  if (!location.state?.products || location.state.products.length === 0) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "48px",
          color: "red",
        }}
      >
        400 page
        <div style={{ fontSize: "22px", marginTop: "8px", fontWeight: 400 }}>
          Bad request — checkout not allowed directly
        </div>
      </div>
    );
  }
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.warning("Enter discount code");
      return;
    }

    try {
      setDiscountLoading(true);

      const res = await fetch(getApiUrl("check-discount"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: discountCode,
          totalPrice,
          cartItems: products,
          userId: userId,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setAppliedDiscount(data.discountAmount);
        setIsDiscountApplied(true);
        sessionStorage.setItem("discountCode", discountCode);
        sessionStorage.setItem("appliedDiscount", data.discountAmount);

        toast.success("Discount applied 🎉");
      } else {
        setAppliedDiscount(0);
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("Discount check failed");
    } finally {
      setDiscountLoading(false);
    }
  };
  // 🔥 RAZORPAY - EXACT SAME STYLE AS PHONEPE ✅
  const handleRazorpayPayment = async () => {
    if (!userDetails?.address) {
      toast.warning("Please save your address first!");
      return;
    }

    if (!allProductsAvailable) {
      toast.error(`Stock Issue!\n${getStockErrorMessage()}`, {
        autoClose: 8000,
      });
      return;
    }

    try {
      const amountInPaise = Math.round(finalPriceWithShipping * 100);

      const res = await fetch(getApiUrl("create-razorpay-order"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountInPaise, // Razorpay expects paise
          products,
          userId,
          finalPrice: finalPriceWithShipping,
          address: {
            name: formik.values.name,
            phone: formik.values.phone,
            state: formik.values.state,
            district: formik.values.district,
            address: formik.values.address,
            email: formik.values.email,
            pincode: formik.values.pincode,
          },
        }),
      });

      const data = await res.json();

      // ✅ Check our server response (not PhonePe)
      if (!data.success) {
        toast.error(data.error || "Payment failed");
        return;
      }

      // Clear discount
      sessionStorage.removeItem("discountCode");
      sessionStorage.removeItem("appliedDiscount");
      setAppliedDiscount(0);
      setIsDiscountApplied(false);

      // Load Razorpay script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID, // from .env
          amount: data.amount, // in paise
          currency: data.currency || "INR",
          name: "BSK Boutique",
          order_id: data.razorpay_order_id, // ✅ use razorpay_order_id
          description: `Order #${data.merchant_order_id}`,
          handler: (response) => {
            // Local success UI; final status will come from webhook / API
            toast.success("Payment Success! Processing order...");
            console.log("Razorpay Response:", response);
          },
          prefill: {
            name: formik.values.name,
            contact: formik.values.phone,
            email: formik.values.email,
          },
          theme: {
            color: "#28a745",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      };

      script.onerror = () => {
        toast.error("Failed to load Razorpay. Please try again.");
      };

      document.body.appendChild(script);
    } catch (err) {
      console.error("Razorpay initiation error:", err);
      toast.error("Payment failed");
    }
  };

  return (
    <div className="container mt-4">
      <ToastContainer position="top-right" autoClose={4000} />
      <h3 className="fw-bold mb-4">Checkout</h3>
      {(!userDetails || !userDetails.address || editMode) && (
        <div className="card p-4 mb-5 shadow-sm">
          <h5 className="mb-3">
            {editMode ? "Edit Checkout Details" : "Add Checkout Details"}
          </h5>

          <form onSubmit={formik.handleSubmit} className="add-product-form">
            <div className="d-flex flex-wrap">
              <div className="floating-label-group col-md-6 col-12 mb-3">
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder=" "
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Name*</label>
                {formik.touched.name && formik.errors.name && (
                  <small className="text-danger">{formik.errors.name}</small>
                )}
              </div>

              <div className="floating-label-group col-md-6 col-12 mb-3">
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  placeholder=" "
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Whatsapp Number*</label>
                {formik.touched.phone && formik.errors.phone && (
                  <small className="text-danger">{formik.errors.phone}</small>
                )}
              </div>

              <div className="floating-label-group col-md-6 col-12 mb-3">
                <select
                  name="state"
                  value={formik.values.state}
                  onChange={handleStateChange}
                  className="form-select"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s._id} value={s.state}>
                      {s.state}
                    </option>
                  ))}
                </select>
                {formik.touched.state && formik.errors.state && (
                  <small className="text-danger">{formik.errors.state}</small>
                )}
              </div>

              <div className="floating-label-group col-md-6 col-12 mb-3">
                <select
                  name="district"
                  value={formik.values.district}
                  onChange={formik.handleChange}
                  className="form-select"
                  disabled={!districts.length}
                >
                  <option value="">Select District</option>
                  {districts.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                {formik.touched.district && formik.errors.district && (
                  <small className="text-danger">
                    {formik.errors.district}
                  </small>
                )}
              </div>

              <div className="floating-label-group col-12 mb-3">
                <textarea
                  name="address"
                  className="form-control"
                  placeholder=" "
                  rows={3}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Address*</label>
                {formik.touched.address && formik.errors.address && (
                  <small className="text-danger">{formik.errors.address}</small>
                )}
              </div>
              <div className="floating-label-group  col-12 mb-3">
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  placeholder=" "
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Pincode*</label>

                {formik.touched.pincode && formik.errors.pincode && (
                  <small className="text-danger">{formik.errors.pincode}</small>
                )}
              </div>

              {/* <div className="floating-label-group col-md-6 col-12 mb-3">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder=" "
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Email (optional)</label>
                {formik.touched.email && formik.errors.email && (
                  <small className="text-danger">{formik.errors.email}</small>
                )}
              </div> */}

              <div className="col-12 text-center">
                <button type="submit" className="btn btn-primary px-4 py-2">
                  {editMode ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      {userDetails && userDetails.address && !editMode && (
        <div className="card p-4 mb-4 shadow-sm">
          <h5 className="mb-3">
            User Details
            <span className="badge bg-success ms-2">Verified Address</span>
          </h5>

          <p>
            <b>Name:</b> {userDetails.name}
          </p>
          <p>
            <b>Phone:</b> {userDetails.phone}
          </p>
          {/* <p>
            <b>Email:</b> {userDetails.email || "-"}
          </p> */}
          <p>
            <b>State:</b> {userDetails.state}
          </p>
          <p>
            <b>District:</b> {userDetails.district}
          </p>
          <p>
            <b>Address:</b> {userDetails.address}
          </p>
          <p>
            <b>Pincode:</b> {userDetails.pincode}
          </p>

          <button
            className="btn btn-warning mt-3"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        </div>
      )}

      <div className="card p-4 mb-4 shadow-sm">
        <h5 className="mb-3">Order Summary</h5>

        {products.map((item, i) => (
          <div key={i} className="mb-2">
            <div className="d-flex justify-content-between">
              <span>{item.name}</span>
              <span>₹{item.price}</span>
            </div>
            <div className="d-flex justify-content-between text-muted small">
              <span>
                Size: <b>{item.size}</b>
              </span>
              <span>Qty: {item.qty}</span>
            </div>
          </div>
        ))}

        <hr />
        <div className="floating-label-group col-12 mb-3 position-relative">
          <div className="d-flex">
            <div className="flex-grow-1 position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder=" "
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              />
              <label>Discount Code</label>
            </div>

            <button
              type="button"
              className="btn btn-dark ms-2 px-4"
              onClick={handleApplyDiscount}
              disabled={discountLoading || isDiscountApplied}
            >
              {isDiscountApplied
                ? "Applied ✓"
                : discountLoading
                  ? "Checking..."
                  : "Apply"}
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <span>Total</span>
          <span>₹{totalPrice}</span>
        </div>

        <div className="d-flex justify-content-between text-success">
          <span>Discount</span>
          <span>- ₹{totalDiscountAmount}</span>
        </div>

        <div className="d-flex justify-content-between">
          <b>Subtotal</b>
          <b>₹{subtotalAfterDiscount}</b>
        </div>

        <hr />

        <div className="d-flex justify-content-between text-primary">
          <span>Shipping</span>
          <span>₹{shippingPrice}</span>
        </div>

        <div className="d-flex justify-content-between fs-5 mt-2">
          <b>Final Price</b>
          <b>₹{finalPriceWithShipping}</b>
        </div>

        <div className="text-center mt-3">
          {gatewaySettings.phonePeEnabled && (
            <button
              className={`btn btn-lg w-100 mb-3 ${canPay && allProductsAvailable ? "btn-success" : "btn-outline-secondary"}`}
              onClick={handlePhonePePayment}
              disabled={!canPay || !allProductsAvailable}
            >
              {canPay && allProductsAvailable
                ? "Pay with PhonePe"
                : !allProductsAvailable
                  ? "Products Unavailable"
                  : "Complete Address"}
            </button>
          )}

          {gatewaySettings.razorpayEnabled && (
            <button
              className={`btn btn-lg w-100 mb-3 ${canPay && allProductsAvailable ? "btn-warning" : "btn-outline-warning"}`}
              onClick={handleRazorpayPayment}
              disabled={!canPay || !allProductsAvailable}
            >
              {canPay && allProductsAvailable
                ? "Pay with Razorpay"
                : !allProductsAvailable
                  ? "Products Unavailable"
                  : "Complete Address"}
            </button>
          )}

          {!allProductsAvailable && stockCheckResult?.outOfStock && (
            <div className="alert alert-danger small py-2">
              <strong>Why disabled:</strong>
              <br />
              {getStockErrorMessage()
                .split("\n")
                .map((msg, i) => (
                  <span key={i} className="d-block">
                    {msg}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
